import {
  AuthTokenErrorType,
  AuthTokenSource,
  IAuthTokenData,
  IAuthTokenError,
  IAuthTokenFactory,
  IAuthTokenOptions,
  IAuthTokenStore,
  ISerializedAuthToken,
  ISourceTokenDebugger,
  TokenDependencies,
} from './authTokenTypes';
import { action, makeAutoObservable } from 'mobx';
import { AuthTokenHelper } from './authTokenHelper';
import { IWallClock } from '../../utils/time/IWallClock';
import { DefaultWallClock } from '../../utils/time/DefaultWallClock';
import { SourceTokenDebugger } from './TokenDebugger';
import {
  ServerCookies,
  WriteableServerCookies,
} from '../../utils/serverCookies';
import { jwtDecode } from 'jwt-decode';
import { DateTime } from 'luxon';
import { clientConfig } from '@portal/env/config';
import { isTokenError } from './isTokenError';
import { BW_SESSIONID_COOKIE_NAME } from './bruntwoodSessionStore';
import { IProfileTokenPayload } from './IProfileTokenPayload';
import { IFetchHelper } from './IFetchHelper';
import { FetchHelper } from './FetchHelper';

function tryParseJSON(json: string): any {
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}
const config = clientConfig();

export const BRUNTWOOD_TOKEN_COOKIE_NAME = 'bruntwood.token.value';

function expiryDateFromToken(token: string): DateTime | null {
  try {
    const decoded = jwtDecode(token) as { exp: number };
    return DateTime.fromSeconds(decoded.exp);
  } catch {
    return null;
  }
}

export class BruntwoodTokenStore implements IAuthTokenStore<IProfileTokenPayload | null> {
  public readonly source = 'bruntwoodToken';

  public readonly isRequired = true;

  public readonly subscriber = null;

  /**
   * We can get a new one of these either from an old one or from the bruntwood session token
   */
  public readonly dependencies: AuthTokenSource[] = [
    'bruntwoodSession',
    'bruntwoodToken',
  ];

  private _value: string | null;
  public readonly usedCookieNames = [BRUNTWOOD_TOKEN_COOKIE_NAME];

  private readonly helper: AuthTokenHelper;
  constructor(
    value: string | null,
    wallClock: IWallClock = DefaultWallClock.instance,
    private readonly tokenDebugger: ISourceTokenDebugger = new SourceTokenDebugger(
      this.source,
    ),
    private readonly fetchHelper: IFetchHelper = new FetchHelper(),
  ) {
    this._value = value;
    this.helper = new AuthTokenHelper(
      {
        wallClock: wallClock,
      },
      value
        ? {
            value,
            expiryDate: expiryDateFromToken(value),
            source: this.source,
          }
        : undefined,
    );
    makeAutoObservable(this);
  }

  get value(): string | null {
    return this._value;
  }

  public removeValue() {
    this._value = null;
  }

  get isReady(): boolean {
    return (
      !!this._value &&
      !this.helper.isInvalidatedByFailures() &&
      !this.helper.isExpired()
    );
  }

  get meta(): IProfileTokenPayload | null {
    if (!this._value) {
      return null;
    }

    return BruntwoodTokenStore.decodeBruntwoodToken(this._value);
  }

  public static decodeBruntwoodToken(tokenString: string) {
    return jwtDecode<IProfileTokenPayload>(tokenString || '');
  }

  get canAttemptAgain(): boolean {
    return this.helper.hasRemainingAttempts();
  }
  get expiryDate(): DateTime | null {
    return this.helper.expiryDate;
  }
  get isExpired(): boolean {
    return this.helper.isExpired();
  }
  get shouldRefresh(): boolean {
    return this.helper.needsRefresh();
  }
  get timeoutExpiry(): DateTime | null {
    return this.helper.backoffExpiry;
  }

  saveCookies(writeableCookies: WriteableServerCookies): void {
    if (this.value) {
      writeableCookies.set(BRUNTWOOD_TOKEN_COOKIE_NAME, this.value);
    } else {
      writeableCookies.clear(BRUNTWOOD_TOKEN_COOKIE_NAME);
    }
    if (this.helper.expiryDate) {
      writeableCookies.setExpiry(
        BRUNTWOOD_TOKEN_COOKIE_NAME,
        this.helper.expiryDate,
      );
    }
  }

  async checkResponse(response: Response): Promise<IAuthTokenError | null> {
    if (response.status === 401) {
      // Generally requests to the auth server will return 401 if the token is invalid
      this.helper.recordFailedRequest();
      this.tokenDebugger.addEvent({
        action: 'fetchFailed',
      });
      return {
        source: this.source,
        type: AuthTokenErrorType.UNAUTHORIZED,
        message: 'Unauthorized',
      };
    }

    return null;
  }

  @action
  update(data: IAuthTokenData): void {
    this.tokenDebugger.addEvent({
      action: 'updated',
    });
    this.helper.recordNewToken(expiryDateFromToken(data.value));
    this._value = data.value;
  }

  private async tryFetchToken(
    headers: Record<string, string>,
  ): Promise<string | IAuthTokenError> {
    const url = `${config.authServiceUrl}/api/token`;
    try {
      this.tokenDebugger.addEvent({
        action: 'updateAttempt',
        message: 'Attempting to update bruntwood token',
      });

      const result = await this.fetchHelper.fetch(url, {
        method: 'POST',
        throwOnHttpError: false,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      const body = tryParseJSON(await result.text());

      if (!result.ok) {
        const message = body?.error || 'Unknown error';
        this.tokenDebugger.addEvent({
          action: 'updateFailed',
          message: `Could not update bruntwood token: ${message}`,
          error: new Error(message),
        });
        return {
          source: this.source,
          type: AuthTokenErrorType.REFRESH_FAILED,
          message: `Could not update bruntwood token: ${message}`,
        };
      }

      if (!body.token) {
        const message = 'Refresh seemed to succeed but no token was returned';
        this.tokenDebugger.addEvent({
          action: 'updateFailed',
          message: `Could not update bruntwood token: ${message}`,
          error: new Error(message),
        });

        return {
          source: this.source,
          type: AuthTokenErrorType.REFRESH_FAILED,
          message: `Could not update bruntwood token: ${message}`,
        };
      }

      return body.token as string;
    } catch (e: any) {
      this.tokenDebugger.addEvent({
        action: 'updateFailed',
        message: `Could not update bruntwood token: ${e?.message}`,
        error: e,
      });

      return {
        source: this.source,
        type: AuthTokenErrorType.REFRESH_FAILED,
        message: `Could not update bruntwood token: ${e?.message}`,
      };
    }
  }

  async updateWith(
    dependencies: TokenDependencies,
  ): Promise<IAuthTokenError | null> {
    // If we've got a valid bruntwood session token, we can use that to get a new bruntwood token
    const bruntwoodSession = dependencies['bruntwoodSession'];
    if (bruntwoodSession) {
      const result = await this.tryFetchToken({
        // Does this even actually get sent on the client??
        Cookie: `${BW_SESSIONID_COOKIE_NAME}=${bruntwoodSession.value}`,
      });
      if (isTokenError(result)) {
        return result;
      } else {
        this.update({
          value: result,
          source: this.source,
          expiryDate: expiryDateFromToken(result),
        });
        return null;
      }
    }

    if (dependencies.bruntwoodToken) {
      const bruntwoodToken = dependencies.bruntwoodToken;
      const result = await this.tryFetchToken({
        Authorization: `Bearer ${bruntwoodToken.value}`,
      });

      if (isTokenError(result)) {
        return result;
      } else {
        this.update({
          value: result,
          source: this.source,
          expiryDate: expiryDateFromToken(result),
        });
        return null;
      }
    }

    return {
      source: this.source,
      type: AuthTokenErrorType.NO_TOKEN,
      message: 'Could not get dependencies for bruntwoodToken',
    };
  }
}

export class BruntwoodTokenStoreFactory implements IAuthTokenFactory<IProfileTokenPayload | null> {
  createEmpty(options?: IAuthTokenOptions) {
    const tokenDebugger =
      options?.debugger?.forSource('bruntwoodToken') ||
      new SourceTokenDebugger('bruntwoodToken');
    tokenDebugger.addEvent({
      action: 'createdEmpty',
      message: 'Created empty BruntwoodTokenStore',
    });

    return new BruntwoodTokenStore(null, options?.wallClock, tokenDebugger);
  }

  createOnClient(
    fromServer: ISerializedAuthToken,
    options?: IAuthTokenOptions,
  ) {
    const tokenDebugger =
      options?.debugger?.forSource('bruntwoodToken') ||
      new SourceTokenDebugger('bruntwoodToken');

    tokenDebugger.addEvent({
      action: 'loadedDuringHydration',
      message: 'Loaded during hydration',
    });

    return new BruntwoodTokenStore(
      fromServer.token,
      options?.wallClock,
      tokenDebugger,
    );
  }

  createOnServer(cookies: ServerCookies, options?: IAuthTokenOptions) {
    const tokenDebugger =
      options?.debugger?.forSource('bruntwoodToken') ||
      new SourceTokenDebugger('bruntwoodToken');

    const value = cookies.getValueOf(BRUNTWOOD_TOKEN_COOKIE_NAME);

    if (value) {
      tokenDebugger.addEvent({
        action: 'loadedFromCookie',
        message: 'Loaded from cookie',
      });
    } else {
      tokenDebugger.addEvent({
        action: 'createdEmpty',
        message: 'Created empty BruntwoodTokenStore',
      });
    }

    return new BruntwoodTokenStore(
      value || null,
      options?.wallClock,
      tokenDebugger,
    );
  }
}
